<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <sms-enable-card
      v-if="!loading && view === 'enable'"
      :group-name="group.name"
      :cancelled-at="settings && settings.registration_status === 'cancelled' ? settings.released_at : null"
      @start-onboarding="startOnboarding"
    />

    <sms-onboarding-wizard
      v-if="!loading && view === 'wizard'"
      :settings="settings"
      :business-prefill="companyPrefill"
      :saving="saving"
      :validation-errors="validationErrors"
      :number-search-results="numberSearchResults"
      :searching-numbers="searchingNumbers"
      :test-result="testResult"
      :sending-test="sendingTest"
      @send-test="handleSendTest"
      @submit-business-profile="handleSubmitBusinessProfile"
      @submit-brand="handleSubmitBrand"
      @submit-campaign="handleSubmitCampaign"
      @search-numbers="handleSearchNumbers"
      @purchase-number="handlePurchaseNumber"
      @activate="handleActivate"
      @release-number="handleReleaseNumber"
      @replace-number="handleReplaceNumber"
      @pause-sms="handlePauseSms"
      @resume-sms="handleResumeSms"
      @cancel="cancelOnboarding"
      @finish="finishOnboarding"
    />
  </div>
</template>

<script>
import { Auth } from "aws-amplify";
import { API_LOCATION } from "@/api-config";
import states from '@/assets/states.json';
import SMSEnableCard from './SMSEnableCard';
import SMSOnboardingWizard from './SMSOnboardingWizard';

// There are two views, not three. An active group keeps showing the wizard with
// every step in its completed/read-only state (business profile summary, brand
// status, campaign id, purchased number). A separate "panel" view used to take
// over at `active` and hid every value the tenant had submitted, including the
// campaign copy carriers vetted, so it was removed rather than rebuilt.
// A cancelled group goes back to the enable card, which explains the cancellation.
const ENABLE_STATUSES = ['not_started', 'cancelled'];

export default {
  name: 'SMSSettings',
  components: {
    'sms-enable-card': SMSEnableCard,
    'sms-onboarding-wizard': SMSOnboardingWizard,
  },
  data() {
    return {
      loading: false,
      saving: false,
      group: {},
      companyId: localStorage.getItem('companyId'),
      companyGroupId: null,
      settings: null,
      validationErrors: [],
      view: 'enable',
      forcedToWizard: false,
      companyPrefill: null,
      pollTimer: null,
      numberSearchResults: [],
      searchingNumbers: false,
      testResult: null,
      sendingTest: false
    };
  },
  mounted() {
    this.loadGroupFromStorage();
    this.loadSettings();
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  },
  methods: {
    loadGroupFromStorage() {
      const raw = localStorage.getItem('group');
      try {
        this.group = raw ? JSON.parse(raw) : {};
      } catch (e) {
        console.warn('SMSSettings: corrupt localStorage.group, falling back to empty', e);
        this.group = {};
      }
      this.companyGroupId = this.group.id || null;
    },
    getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => resolve(data.idToken.jwtToken))
          .catch(err => {
            console.log(err);
            resolve(null);
          });
      });
    },
    deriveView(settings) {
      if (this.forcedToWizard) return 'wizard';
      if (!settings || !settings.exists) return 'enable';
      const status = settings.registration_status;
      if (ENABLE_STATUSES.includes(status)) return 'enable';
      return 'wizard';
    },
    async loadSettings(options = {}) {
      if (!this.companyId || !this.companyGroupId) {
        console.warn('SMSSettings: missing companyId or companyGroupId');
        return;
      }

      if (!options.silent) this.loading = true;
      try {
        const token = await this.getAuthToken();
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-settings`;
        const response = await this.axios.get(url, { headers: { Authorization: token } });
        this.settings = response.data;
        this.view = this.deriveView(this.settings);

        // Prefill the business profile form from company settings when the
        // wizard has nothing saved yet - saved profile values always win.
        const hasSavedProfile = this.settings?.business_profile
          && Object.keys(this.settings.business_profile).length > 0;
        if (!hasSavedProfile) {
          await this.loadCompanyPrefill(token);
        }
        this.ensureReviewPolling();
      } catch (err) {
        console.log(err);
        this.settings = { exists: false, registration_status: 'not_started', business_profile: null };
        this.view = 'enable';
        this.$notify({
          type: 'danger',
          message: 'Could not load SMS settings. Please refresh the page.',
          icon: 'tim-icons icon-bell-55'
        });
      } finally {
        if (!options.silent) this.loading = false;
      }
    },
    // While Twilio is reviewing (any *_review status), silently refresh the
    // settings every 60 seconds so the wizard advances without user action
    // (same cadence as AnnouncementsBell). The interval is cleared the moment
    // the status leaves review and on destroy.
    ensureReviewPolling() {
      const inReview = /_review$/.test(this.settings?.registration_status || '');
      if (inReview && !this.pollTimer) {
        this.pollTimer = setInterval(() => this.loadSettings({ silent: true }), 60000);
      } else if (!inReview && this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    // Maps GET companies/{id} onto business_profile field names. Company
    // settings stores the state as the full name; the profile needs the
    // 2-letter code. Rep fields are NOT prefilled - the company contact is
    // not necessarily the authorized representative, and a wrong prefill
    // there risks failing carrier verification.
    async loadCompanyPrefill(token) {
      try {
        const url = `${API_LOCATION}companies/${this.companyId}`;
        const response = await this.axios.get(url, { headers: { Authorization: token } });
        const company = response.data.company || {};
        const socials = response.data.social_media || [];

        const prefill = {};
        if (company.name) prefill.legal_business_name = company.name;
        if (company.address) prefill.address_line_1 = company.address;
        if (company.address_line_2) prefill.address_line_2 = company.address_line_2;
        if (company.city) prefill.city = company.city;
        if (company.zip) prefill.zip = company.zip;
        if (typeof company.country === 'string' && company.country.length === 2) {
          prefill.country = company.country;
        }
        if (company.state) {
          const match = states.states.find(
            (s) => s.name === company.state || s.code === company.state
          );
          if (match) prefill.state = match.code;
        }
        const website = socials.find((s) => s.type_id === 5 && s.link);
        if (website) {
          prefill.website = /^https?:\/\//i.test(website.link)
            ? website.link
            : `https://${website.link}`;
        }

        this.companyPrefill = Object.keys(prefill).length ? prefill : null;
      } catch (err) {
        // Prefill is a convenience - never block onboarding on it.
        console.log('SMSSettings: could not load company prefill', err);
        this.companyPrefill = null;
      }
    },
    startOnboarding() {
      this.validationErrors = [];
      this.forcedToWizard = true;
      this.view = 'wizard';
    },
    cancelOnboarding() {
      this.validationErrors = [];
      this.forcedToWizard = false;
      this.view = this.deriveView(this.settings);
    },
    finishOnboarding() {
      this.validationErrors = [];
      this.forcedToWizard = false;
      this.loadSettings();
    },
    // Shared POST for the onboarding actions. Success merges the reshaped
    // settings row and re-arms review polling; 400s with validation_errors
    // fan out to the step fields; anything else surfaces the server's detail.
    async performAction(action, data, successMessage) {
      if (!this.companyId || !this.companyGroupId) return false;

      this.saving = true;
      this.validationErrors = [];
      try {
        const token = await this.getAuthToken();
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-settings`;
        const body = { action };
        if (data) body.data = data;
        const response = await this.axios.post(url, body, { headers: { Authorization: token } });

        this.settings = { ...this.settings, exists: true, ...response.data };
        this.ensureReviewPolling();
        if (successMessage) {
          this.$notify({
            type: 'success',
            message: successMessage,
            icon: 'tim-icons icon-check-2'
          });
        }
        return true;
      } catch (err) {
        const status = err.response?.status;
        const raw = err.response?.data;
        let parsed = raw;
        if (typeof raw === 'string') {
          try { parsed = JSON.parse(raw); } catch (e) { parsed = { error: raw }; }
        }
        if (status === 400 && Array.isArray(parsed?.validation_errors)) {
          this.validationErrors = parsed.validation_errors;
          this.$notify({
            type: 'danger',
            message: `Please fix ${parsed.validation_errors.length} ${parsed.validation_errors.length === 1 ? 'error' : 'errors'} below.`,
            icon: 'tim-icons icon-alert-circle-exc'
          });
        } else {
          const detail = parsed?.twilio_message || parsed?.error || 'Something went wrong. Please try again.';
          this.notifyError(detail);
        }
        return false;
      } finally {
        this.saving = false;
      }
    },
    async handleSubmitCampaign(data) {
      await this.performAction('submit_campaign', data, 'Campaign submitted for verification');
    },
    async handleSearchNumbers(data) {
      if (!this.companyId || !this.companyGroupId) return;

      this.searchingNumbers = true;
      try {
        const token = await this.getAuthToken();
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-settings`;
        const response = await this.axios.post(url, { action: 'search_numbers', data }, { headers: { Authorization: token } });
        this.numberSearchResults = response.data?.numbers || [];
      } catch (err) {
        const raw = err.response?.data;
        let parsed = raw;
        if (typeof raw === 'string') {
          try { parsed = JSON.parse(raw); } catch (e) { parsed = { error: raw }; }
        }
        this.numberSearchResults = [];
        this.notifyError(parsed?.twilio_message || parsed?.error || 'Could not search for numbers. Please try again.');
      } finally {
        this.searchingNumbers = false;
      }
    },
    async handlePurchaseNumber(data) {
      const ok = await this.performAction('purchase_number', data, 'Number purchased');
      if (ok) this.numberSearchResults = [];
    },
    async handleActivate(data) {
      // No success claim about the text itself: activate returns the real
      // delivery outcome and the test step reports it.
      await this.performAction('activate', data, 'SMS is now active');
    },
    async handleSendTest(data) {
      if (!this.companyId || !this.companyGroupId) return;
      this.sendingTest = true;
      this.testResult = null;
      try {
        const token = await this.getAuthToken();
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-settings`;
        const response = await this.axios.post(url, { action: 'send_test', data }, { headers: { Authorization: token } });
        this.testResult = response.data || null;
      } catch (err) {
        const raw = err.response?.data;
        let parsed = raw;
        if (typeof raw === 'string') {
          try { parsed = JSON.parse(raw); } catch (e) { parsed = { error: raw }; }
        }
        this.notifyError(parsed?.twilio_message || parsed?.error || 'Could not send the test. Please try again.');
      } finally {
        this.sendingTest = false;
      }
    },
    notifyError(message) {
      this.$notify({
        type: 'danger',
        message,
        icon: 'tim-icons icon-alert-circle-exc'
      });
    },
    // Cancel SMS: the wizard's test step confirms first, then the number is
    // released and the group lands on the enable card as cancelled.
    async handleReleaseNumber() {
      const ok = await this.performAction('release_number', null, 'SMS cancelled and number released');
      if (ok) {
        this.forcedToWizard = false;
        this.view = this.deriveView(this.settings);
      }
    },
    // Replace number: the old number is released and the status drops to
    // number_pending, which the wizard's status watcher turns into the number step.
    async handleReplaceNumber() {
      await this.performAction('replace_number', null, 'Number released. Choose a new number to continue.');
    },
    async handlePauseSms() {
      await this.performAction('pause_sms', null, 'SMS paused. Your number is kept until you resume.');
    },
    async handleResumeSms() {
      await this.performAction('resume_sms', null, 'SMS resumed');
    },
    async handleSubmitBrand() {
      await this.performAction('submit_brand', null, 'Brand registration submitted');
    },
    async handleSubmitBusinessProfile(profile) {
      if (!this.companyId || !this.companyGroupId) return;

      this.saving = true;
      this.validationErrors = [];

      try {
        const token = await this.getAuthToken();
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/sms-settings`;
        const body = { action: 'submit_profile', data: profile };

        const response = await this.axios.post(url, body, { headers: { Authorization: token } });

        this.settings = {
          ...this.settings,
          exists: true,
          ...response.data
        };

        this.ensureReviewPolling();
        this.$notify({
          type: 'success',
          message: 'Business profile saved',
          icon: 'tim-icons icon-check-2'
        });
      } catch (err) {
        const status = err.response?.status;
        const data = err.response?.data;

        if (status === 400 && data) {
          let parsed = data;
          if (typeof data === 'string') {
            try { parsed = JSON.parse(data); } catch (e) { parsed = { error: data }; }
          }
          if (Array.isArray(parsed.validation_errors)) {
            this.validationErrors = parsed.validation_errors;
            this.$notify({
              type: 'danger',
              message: `Please fix ${parsed.validation_errors.length} ${parsed.validation_errors.length === 1 ? 'error' : 'errors'} below.`,
              icon: 'tim-icons icon-alert-circle-exc'
            });
          } else {
            this.notifyError(parsed.error || 'Validation failed.');
          }
        } else {
          console.log(err);
          this.notifyError('Could not save your business profile. Please try again.');
        }
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
